<script setup lang="ts">
  import type { RipType } from '@/components/release-in-progress/types'
  import type { IndexPageStoryblok } from '@/storyblok/component-types-sb'
  import { usePageType } from '@/composables/usePageType'
  import { CSS } from '@/utils/constants/rip'
  import { useBookingVerificationDataLayer } from '@/composables/data-layers/useBookingVerificationDataLayer'

  defineProps<{
    blok: IndexPageStoryblok
  }>()

  const { ripStatus, ripTexts } = useGlobalSettingsStore()
  const { pageType } = usePageType()
  const { pushIndexPageErrorDataLayer } = useBookingVerificationDataLayer()
  const bookingVerificationData = useBookingVerificationData()
  const route = useRoute()
  const isIndexPage = computed(() => route.meta.pageType === 'index')
  const ripPageType = computed<RipType | undefined>(() => (pageType.value === CSS ? undefined : pageType.value))

  const ripTextsData = computed(() => {
    if (!ripPageType.value) return undefined

    if (!ripStatus?.[ripPageType.value]) return undefined

    return ripTexts?.[ripPageType.value] ?? undefined
  })

  const errorMessage = computed(() => {
    if (!bookingVerificationData.value || !isIndexPage.value) return null

    const { serverError, errorRef } = bookingVerificationData.value
    if (!serverError) return null

    if (errorRef && errorRef !== '') {
      return `${serverError} (ref: ${errorRef})`
    }

    return serverError
  })

  onMounted(() => {
    if (!isIndexPage.value || !bookingVerificationData.value || !errorMessage.value) return

    pushIndexPageErrorDataLayer(bookingVerificationData.value)
  })
</script>

<template>
  <div class="mb-20 flex flex-col justify-start gap-16 md:flex-row xl:gap-36">
    <h1
      v-if="blok.h1Text"
      class="sr-only"
    >
      {{ blok.h1Text }}
    </h1>

    <section class="order-0 flex w-full grow flex-col gap-6 md:order-1">
      <div
        v-if="errorMessage"
        class="mx-2 mt-6 md:mx-10"
      >
        <UiAlert
          variant="error"
          :closable="true"
          :text="errorMessage"
        />
      </div>

      <template
        v-for="body in blok.body"
        :key="body._uid"
      >
        <ReleaseInProgress
          v-if="body.component === 'wide-hero' && ripPageType && ripStatus?.[ripPageType]"
          :page="ripPageType"
          :texts="ripTextsData"
        />
        <ScrollingBanner
          v-else-if="body.component === 'scrolling-banner'"
          :blok="body"
          class="mx-2 md:mx-10"
        />
        <StoryblokComponent
          v-else
          v-editable="body"
          :blok="body"
        />
      </template>
    </section>
  </div>
</template>
